import React, { FC, ReactNode, useCallback, useContext, useRef } from 'react';
import { ShareableTable } from './data';
import { DataRoomShareableLink } from 'models/ShareableLink';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import usePastTime from 'hooks/usePastTime';
import { Button } from 'ncoded-component-library';
import SettingsIcon from 'icons/Settings.icon';
import {
  getIsAccessAddPasword,
  getIsAccessAnyone,
} from 'router/subrouters/DataRoom/utils';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import ClipboardcopyIcon from 'icons/Clipboardcopy.icon';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import {
  getArrayLength,
  getResolveUrl,
  getShareableLinkRoute,
  isShareableLinkNdaProcessing,
  isUndefined,
} from 'utils';
import TrashIcon from 'icons/Trash.icon';
import RefreshIcon from 'icons/Refresh.icon';
import {
  SL_NDA_COPY,
  SL_NDA_STATUS,
} from 'router/subrouters/DataRoom/models/DataRoomNDA';
import { NDATemplate } from 'models/Nda';
import NdaListModal from 'components/NdaListModal';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import CalendarIcon from 'icons/Calendar.icon';
import WarningExclamationIcon from 'icons/WarningExclamation.icon';
import ArrayPopup from 'components/ArrayPopup';
import TransWithNda from 'components/TransWithNda';
import {
  generateDaysLeftString,
  getFormattedDate,
  isDateInNextDays,
} from 'dateUtils';
import useChangeShareableLinkState from '../../../hooks/useChangeShareableLinkState';
import useManageShareableLink from '../../../hooks/useManageShareableLink';
import useProlongSecureLink from 'router/subrouters/DataRoom/pages/Dashboard/componenets/SuggestedActions/hooks/useProlongSecureLink';

const MAX_NUM_OF_PEOPLE = 2;

type CellProps = {
  link: DataRoomShareableLink;
  updateShareableLink?: (link: DataRoomShareableLink) => void;
};

const NameCell: FC<CellProps> = ({ link }) => {
  const { name } = link;

  return <div className="name-cell">{name}</div>;
};

export const NumberAcessCell: FC<CellProps> = ({ link }) => {
  const {
    linkAccess,
    anyoneWithNonPublicDomain,
    emailDomain,
    recipientEmailAddresses,
  } = link;

  const { t } = useTranslation();

  const isAccessAnyone = getIsAccessAnyone(linkAccess);
  const isAccessAnyoneWithPassword = getIsAccessAddPasword(linkAccess);

  return (
    <div className="number-access-cell">
      {isAccessAnyone ? (
        t('anyoneWithLink')
      ) : isAccessAnyoneWithPassword ? (
        t('anyoneWithPassword')
      ) : anyoneWithNonPublicDomain ? (
        <>
          {t('anyoneWithDomain')} <b>{emailDomain}</b>
        </>
      ) : (
        <ArrayPopup
          maxLength={MAX_NUM_OF_PEOPLE}
          list={recipientEmailAddresses}
          contentClassName="number-cell__popup"
          portalClassName="number-cell__portal"
          popupText="numberOfPeople"
        />
      )}
    </div>
  );
};

const FilesCell: FC<CellProps> = ({ link }) => {
  const { fileIds } = link;

  return <div className="files-cell">{getArrayLength(fileIds)}</div>;
};

const LastAccessCell: FC<CellProps> = ({ link }) => {
  const { lastAccessDate } = link;

  const pastTime = usePastTime();

  const { t } = useTranslation();

  return (
    <div className="last-access-cell">
      {lastAccessDate ? pastTime(lastAccessDate) : t('nobodyAccessed')}
    </div>
  );
};

export const VisitsCell: FC<CellProps> = ({ link }) => {
  const { totalAccessCount } = link;

  const { t } = useTranslation();

  if (isUndefined(totalAccessCount)) return t('na');

  return <div className="visits-cell">{totalAccessCount || 0}</div>;
};

export const CreatedCell: FC<CellProps> = ({ link }) => {
  const { createdAt } = link;

  const pastTime = usePastTime();

  return <div className="created-cell">{pastTime(createdAt)}</div>;
};

export const ExpiresOnCell: FC<CellProps> = ({ link }) => {
  const { isActive, linkExpiresDate } = link;

  const { t } = useTranslation();

  if (!linkExpiresDate) return null;

  if (isActive && isDateInNextDays(linkExpiresDate, 7)) {
    return (
      <div className="expires-cell expires-cell--soon">
        <WarningExclamationIcon />
        {`${t('expires')} ${generateDaysLeftString(linkExpiresDate)}`}
      </div>
    );
  }

  return (
    <div className="expires-cell">{getFormattedDate(linkExpiresDate)}</div>
  );
};

export const ActiveCell: FC<CellProps> = ({ link, updateShareableLink }) => {
  const { _id, linkExpiresDate } = link;

  const { t } = useTranslation();

  const { isActiveLink, isChangeStateDisabled, changeState } =
    useChangeShareableLinkState({
      link,
      updateShareableLink,
    });

  const onSuccessProlong = useCallback(
    (shareableLink: Partial<DataRoomShareableLink>) => {
      updateShareableLink?.({ ...link, ...shareableLink });
    },
    [link, updateShareableLink],
  );

  const { prolongSecureLinkModal, openProlongModal } =
    useProlongSecureLink(onSuccessProlong);

  return (
    <div className="active-cell" onClick={(e) => e.stopPropagation()}>
      <Button
        variant="link"
        className={classNames({ alert: isActiveLink })}
        disabled={isChangeStateDisabled}
        icon={isActiveLink ? TrashIcon : RefreshIcon}
        onClick={changeState}
      >
        {t(isActiveLink ? 'revoke' : 'renew')}
      </Button>

      {isActiveLink && (
        <>
          <Button
            variant="link"
            disabled={isChangeStateDisabled}
            onClick={() => openProlongModal(_id, linkExpiresDate)}
            icon={CalendarIcon}
          >
            {t('prolong')}
          </Button>
          {prolongSecureLinkModal}
        </>
      )}
    </div>
  );
};

export const CopyCell: FC<CellProps> = ({ link }) => {
  const { _id } = link;

  const copyToClipboard = useCopyToClipboard();

  return (
    <Button
      className="copy-cell"
      variant="icon"
      icon={ClipboardcopyIcon}
      onClick={(e) =>
        copyToClipboard(e, getResolveUrl(getShareableLinkRoute(_id)))
      }
    />
  );
};

export const LinkManageCell: FC<CellProps> = ({ link }) => {
  const { isManageShareableLinkDisabled, manageShareableLink } =
    useManageShareableLink(link);

  return (
    <Button
      className="manage-cell"
      variant="icon"
      disabled={isManageShareableLinkDisabled}
      icon={SettingsIcon}
      onClick={(e) => {
        e.stopPropagation();

        manageShareableLink();
      }}
    />
  );
};

export const ActionsCell: FC<CellProps> = ({ link, updateShareableLink }) => {
  return (
    <div className="actions-cell">
      <ActiveCell link={link} updateShareableLink={updateShareableLink} />
      <CopyCell link={link} />
      <LinkManageCell link={link} />
    </div>
  );
};

const NdaCell: FC<CellProps> = ({ link, updateShareableLink }) => {
  const { dataRoomNdaId, dataRoomNda } = link;

  const { status, envelopeTemplateId, name } = dataRoomNda || {};

  const modalRef = useRef<OverlayRef>();

  const { isSuperAdmin } = useContext(CurrentUserContext);

  const { t } = useTranslation();

  const isProcessing = isShareableLinkNdaProcessing(status);

  const canConnectNda = isSuperAdmin && !envelopeTemplateId && isProcessing;

  const classes = classNames('nda-cell', status);

  const onUploadNda = useCallback(
    async (template: NDATemplate) => {
      await dataRoomApi.nda.linkEnvelopeTemplate(
        dataRoomNdaId,
        template.templateId,
        template.name,
      );

      updateShareableLink({
        ...link,
        dataRoomNdaId: template.templateId,
        dataRoomNda: {
          ...dataRoomNda,
          status: SL_NDA_STATUS.ACTIVE,
          envelopeTemplateId: template.templateId,
        },
      });
    },
    [dataRoomNdaId, dataRoomNda, link, updateShareableLink],
  );

  if (!status) return <div className={classes}>{t('noNda')}</div>;

  return (
    <>
      <div className={classes}>
        <TransWithNda
          i18nKey={SL_NDA_COPY[status]}
          envelopeTemplateName={name}
        />

        {canConnectNda && (
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              modalRef?.current.open();
            }}
          >
            {t('connectNda')}
          </Button>
        )}
      </div>
      {canConnectNda && (
        <div onClick={(e) => e.stopPropagation()}>
          <NdaListModal
            ref={modalRef}
            description={t('connectNondisclosureAgrementWithSecureLink')}
            onSelectNda={onUploadNda}
          />
        </div>
      )}
    </>
  );
};

export const ProtectionCell: FC<CellProps> = ({
  link,
  updateShareableLink,
}) => {
  const {
    includeNdaProtection,
    dataRoomNda,
    includeTermsAndConditionProtection,
  } = link;

  const { t } = useTranslation();

  if (includeNdaProtection && dataRoomNda)
    return <NdaCell link={link} updateShareableLink={updateShareableLink} />;

  return (
    <div className="protection-cell">
      {t(includeTermsAndConditionProtection ? 'tAndC' : 'noNDA')}
    </div>
  );
};

const AccessCell: FC<CellProps> = (props) => (
  <div className="link-access-cell">
    <NumberAcessCell {...props} />
    {' + '}
    <ProtectionCell {...props} />
  </div>
);

export const BODY_CONTENT_GENERATE: Partial<
  Record<keyof ShareableTable, (props: CellProps) => ReactNode>
> = {
  name: (props) => <NameCell {...props} />,
  accessNumber: (props) => <AccessCell {...props} />,
  fileIds: (props) => <FilesCell {...props} />,
  lastAccessDate: (props) => <LastAccessCell {...props} />,
  totalAccessCount: (props) => <VisitsCell {...props} />,
  createdAt: (props) => <CreatedCell {...props} />,
  linkExpiresDate: (props) => <ExpiresOnCell {...props} />,
  linkAccess: (props) => <ActionsCell {...props} />,
};
